import React from 'react';

import { debounce } from 'lodash-es';
import { injectIntl, InjectedIntl } from 'react-intl';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import Lozenge from '@atlaskit/lozenge';
import { Inline } from '@atlaskit/primitives';
import Select, {
  CheckboxSelect,
  OptionType,
  ActionMeta,
} from '@atlaskit/select';
import { gridSize } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';

import SearchField from 'src/components/search-field';
import { BRANCH_KIND } from 'src/constants/branching-model';
import { selectControlStyles } from 'src/styles/select';

import * as filterStyles from '../../../styles/filters';
import { BranchingModel, BranchType, Filter, Status } from '../types';

import messages from './branch-list-filters.i18n';
import * as styles from './branch-list-filters.styled';

type Props = {
  intl: InjectedIntl;
  filter: Filter;
  branchingModel: BranchingModel;
  onFilterChange: (filters: Partial<Filter>) => void;
};

type State = {
  currentSearch: string;
  lastSearch: string;
};

type StatusOptions = {
  [key: string]: {
    label: string;
    value: Status;
  };
};

const BRANCH_TYPE_VALUE_LABELS = {
  [BRANCH_KIND.FEATURE]: messages.branchTypeValueLabelFeature,
  [BRANCH_KIND.BUG_FIX]: messages.branchTypeValueLabelBugfix,
  [BRANCH_KIND.HOT_FIX]: messages.branchTypeValueLabelHotfix,
  [BRANCH_KIND.RELEASE]: messages.branchTypeValueLabelRelease,
};

class BranchListFilters extends React.PureComponent<Props, State> {
  declare context: any;

  state = {
    currentSearch: this.props.filter.search,
    lastSearch: this.props.filter.search,
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (nextProps.filter.search !== prevState.lastSearch) {
      return {
        currentSearch: nextProps.filter.search,
        lastSearch: nextProps.filter.search,
      };
    }

    return null;
  }

  hasStaleBranchesFilter = FeatureGates.checkGate('stale-branch-list-filter');

  statusOptions: StatusOptions = {
    all: {
      label: this.props.intl.formatMessage(messages.statusAll),
      value: Status.All,
    },
    active: {
      label: this.props.intl.formatMessage(messages.statusActive),
      value: Status.Active,
    },
    merged: {
      label: this.props.intl.formatMessage(messages.statusMerged),
      value: Status.Merged,
    },
  };

  handleSearchChange = debounce(
    (search: string) => this.props.onFilterChange({ search }),
    400
  );

  handleSelectBranchType = (option: OptionType[], _action: ActionMeta) => {
    const kinds = option ? option.map(o => o.value) : [];
    this.props.onFilterChange({ branchtype: kinds as BRANCH_KIND[] });
  };

  branchTypeOption = (t: BranchType): OptionType => {
    const { intl } = this.props;
    // @ts-ignore TODO: fix noImplicitAny error here
    const label = intl.formatMessage(BRANCH_TYPE_VALUE_LABELS[t.kind]);
    return {
      label,
      value: t.kind,
      prefix: t.prefix,
    };
  };

  notifyFilterChanged = (search: string) => {
    this.props.onFilterChange({ search });
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ currentSearch: e.target.value }, () =>
      this.handleSearchChange(this.state.currentSearch.trim())
    );
  };

  clearFilter = () => {
    const currentSearch = '';
    this.setState({ currentSearch });
    this.notifyFilterChanged(currentSearch);
  };

  renderBranchTypeFilter() {
    const {
      branchingModel: { branchTypes },
      filter: { branchtype },
      intl,
    } = this.props;

    if (!branchTypes || !branchTypes.length) {
      return null;
    }

    const branchTypeOptions = branchTypes.map(this.branchTypeOption);

    const selectedBranchTypes = branchtype
      ? branchtype
          .map(t => branchTypeOptions.find(o => o.value === t))
          .filter(Boolean)
      : [];

    // @ts-ignore TODO: fix noImplicitAny error here
    const formatOptionLabel = (option, { context }) => {
      if (context === 'menu') {
        // Show label with prefix in opened dropdown menu, e.g. "Feature  feature/"
        const { prefix } = option;
        return (
          <styles.BranchTypeOptionWrapper>
            <div>{option.label}</div>
            <Tooltip content={prefix} position="mouse">
              <styles.BranchTypeOptionPrefix>
                {prefix}
              </styles.BranchTypeOptionPrefix>
            </Tooltip>
          </styles.BranchTypeOptionWrapper>
        );
      } else {
        return option.label;
      }
    };

    return (
      <styles.BranchTypeFilter>
        <CheckboxSelect
          formatOptionLabel={formatOptionLabel}
          isSearchable={false}
          label="Branch Type Select"
          onChange={this.handleSelectBranchType}
          options={branchTypeOptions}
          isMulti
          placeholder={intl.formatMessage(messages.branchTypePlaceholder)}
          value={selectedBranchTypes as OptionType[]}
          styles={{
            // @ts-ignore TODO: fix noImplicitAny error here
            control: (css, state) => ({
              ...css,
              ...filterStyles.subtleSelector(css, state),
              ...selectControlStyles,
            }),
            // @ts-ignore TODO: fix noImplicitAny error here
            menu: css => ({
              ...css,
              width: 'max-content',
              maxWidth: gridSize() * 40,
            }),
            // @ts-ignore TODO: fix noImplicitAny error here
            placeholder: css => ({
              ...css,
              color: 'inherit',
            }),
          }}
        />
      </styles.BranchTypeFilter>
    );
  }

  render() {
    const {
      filter: { status },
      onFilterChange,
      intl,
    } = this.props;

    if (this.hasStaleBranchesFilter) {
      this.statusOptions.stale = {
        label: this.props.intl.formatMessage(messages.statusStale),
        value: Status.Stale,
      };
    }

    const statusOptions = Object.keys(this.statusOptions).map(key => {
      // @ts-ignore TODO: fix noImplicitAny error here
      const option = this.statusOptions[key];
      if (option.value === Status.Stale) {
        return {
          ...option,
          label: (
            <Inline alignBlock="center" space="space.025">
              {option.label}
              <Lozenge appearance="new">Beta</Lozenge>
            </Inline>
          ),
        };
      }
      return option;
    });

    return (
      <filterStyles.FilterGroup>
        <SearchField
          currentSearch={this.state.currentSearch}
          handleFilterChange={this.handleChange}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          clearFilter={this.clearFilter}
          fieldName="search-branches-input"
        />
        <filterStyles.BranchSelectorWrapper>
          <Select
            label={this.statusOptions[status]?.label}
            options={statusOptions}
            isSearchable={false}
            value={this.statusOptions[status]}
            onChange={option => {
              if (option) {
                onFilterChange({ status: option.value });
              }
            }}
            styles={{
              control: (css, state) => {
                const [{ value }] = state.getValue();
                return {
                  ...css,
                  ...filterStyles.subtleSelector(css, state),
                  ...selectControlStyles,
                  width:
                    value === Status.All
                      ? gridSize() * 16
                      : value === Status.Active
                      ? gridSize() * 19
                      : gridSize() * 20,
                };
              },
              menu: css => ({
                ...css,
                width: gridSize() * 20,
              }),
            }}
          />
        </filterStyles.BranchSelectorWrapper>
        {this.renderBranchTypeFilter()}
      </filterStyles.FilterGroup>
    );
  }
}

export default injectIntl(BranchListFilters);
